import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Power, Clock, Shield } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


export default function MaintenanceMode() {
  const [settings, setSettings] = useState({
    enabled: false,
    message: "Die Seite wird gerade gewartet. Bitte versuche es später erneut.",
    expected_end: "",
    allow_admins: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/admin/maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        enabled: res.data.enabled || false,
        message: res.data.message || "Die Seite wird gerade gewartet. Bitte versuche es später erneut.",
        expected_end: res.data.expected_end || "",
        allow_admins: res.data.allow_admins !== false
      });
    } catch (error) {
      console.error("Error fetching maintenance settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleToggle = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/admin/maintenance/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSettings(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Fehler beim Umschalten des Wartungsmodus");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API}/admin/maintenance`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Einstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card className={settings.enabled ? "border-red-500 bg-red-50" : "border-[#001D24] bg-[#001D24]/10"}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {settings.enabled ? (
                <div className="p-3 bg-red-500 rounded-full">
                  <AlertTriangle className="h-8 w-8 text-white" />
                </div>
              ) : (
                <div className="p-3 bg-[#001D24] rounded-full">
                  <Power className="h-8 w-8 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold">
                  {settings.enabled ? "Wartungsmodus AKTIV" : "Seite ist Online"}
                </h3>
                <p className="text-muted-foreground">
                  {settings.enabled
                    ? "Besucher sehen die Wartungsseite"
                    : "Alle Besucher können die Seite normal nutzen"}
                </p>
              </div>
            </div>
            <Button
              onClick={handleToggle}
              disabled={saving}
              variant={settings.enabled ? "destructive" : "default"}
              size="lg"
              className="min-w-[200px]"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Power className="h-5 w-5 mr-2" />
                  {settings.enabled ? "Wartungsmodus beenden" : "Wartungsmodus aktivieren"}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Wartungsmodus Einstellungen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Wartungsnachricht</Label>
            <Textarea
              id="message"
              value={settings.message}
              onChange={(e) => setSettings(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Nachricht für Besucher während der Wartung..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Diese Nachricht wird Besuchern angezeigt, wenn der Wartungsmodus aktiv ist.
            </p>
          </div>

          {/* Expected End */}
          <div className="space-y-2">
            <Label htmlFor="expected_end" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Voraussichtliches Ende
            </Label>
            <Input
              id="expected_end"
              type="datetime-local"
              value={(() => {
                if (!settings.expected_end) return "";
                const d = new Date(settings.expected_end);
                const pad = (n) => n.toString().padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              })()}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                expected_end: e.target.value ? new Date(e.target.value).toISOString() : ""
              }))}
            />
            <p className="text-xs text-muted-foreground">
              Optional: Zeigt Besuchern, wann die Seite voraussichtlich wieder online ist.
            </p>
          </div>

          {/* Allow Admins */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-base">Admin-Zugang erlauben</Label>
              <p className="text-sm text-muted-foreground">
                Admins können sich auch im Wartungsmodus anmelden
              </p>
            </div>
            <Switch
              checked={settings.allow_admins}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, allow_admins: checked }))}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : null}
              Einstellungen speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      {settings.enabled && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Vorschau der Wartungsseite</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-8 text-center">
              <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Wartungsarbeiten</h2>
              <p className="text-muted-foreground mb-4">{settings.message}</p>
              {settings.expected_end && (
                <p className="text-sm text-muted-foreground">
                  Voraussichtlich wieder online: {new Date(settings.expected_end).toLocaleString('de-DE')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
